/**
 * OnCallScheduleCard
 *
 * Admin self-service: weekly on-call schedule management. Reads/writes
 * /api/v1/admin/settings/on-call-schedule. Reuses the same role gating
 * as OpsAlertPreferencesCard (SCRUM-1798) — non-admin renders nothing.
 *
 * The PUT API is full-replacement; this UI mirrors that by sending the
 * entire current entries array on every save. There is no partial-edit
 * mode — adding/removing/toggling all flow through one save action.
 *
 * @story SCRUM-1803
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiRequest from '../../services/api';

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DEFAULT_TIMEZONE = 'America/Denver';

// Common IANA timezones for the selector. Browsers expose Intl.supportedValuesOf
// in modern envs, but we keep an explicit small list for predictable UX at
// launch scale (2-3 admins, all US-based).
const TIMEZONE_OPTIONS = [
  'America/Denver',
  'America/Los_Angeles',
  'America/Chicago',
  'America/New_York',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
];

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/;

function normalizeTime(value) {
  // Accepts HH:MM or HH:MM:SS; outputs HH:MM for the input element.
  if (typeof value !== 'string') { return ''; }
  const m = /^([01]\d|2[0-3]):([0-5]\d)/.exec(value);
  return m ? `${m[1]}:${m[2]}` : '';
}

export default function OnCallScheduleCard() {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [entries, setEntries] = useState([]);

  const isAdmin =
    typeof hasRole === 'function' && (hasRole('admin') || hasRole('super_admin'));

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadError(null);
        const res = await apiRequest('/api/v1/admin/settings/on-call-schedule');
        if (cancelled) { return; }
        if (res && res.success && res.data && Array.isArray(res.data.entries)) {
          setEntries(res.data.entries.map(e => ({
            ...e,
            start_time: normalizeTime(e.start_time),
            end_time: normalizeTime(e.end_time),
          })));
        }
      } catch (err) {
        if (!cancelled) { setLoadError(err.message || 'Failed to load schedule'); }
      } finally {
        if (!cancelled) { setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  function addEntry(day_of_week) {
    setEntries(prev => [
      ...prev,
      {
        day_of_week,
        start_time: '09:00',
        end_time: '17:00',
        timezone: DEFAULT_TIMEZONE,
        is_active: true,
      },
    ]);
    setSaveSuccess(false);
  }

  function updateEntry(index, patch) {
    setEntries(prev => prev.map((e, i) => (i === index ? { ...e, ...patch } : e)));
    setSaveSuccess(false);
  }

  function removeEntry(index) {
    setEntries(prev => prev.filter((_, i) => i !== index));
    setSaveSuccess(false);
  }

  function entriesValid() {
    return entries.every(e =>
      typeof e.day_of_week === 'number'
      && e.day_of_week >= 0 && e.day_of_week <= 6
      && TIME_RE.test(e.start_time)
      && TIME_RE.test(e.end_time)
      && typeof e.timezone === 'string' && e.timezone.length > 0
    );
  }

  async function handleSave() {
    if (!entriesValid() || saving) { return; }
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const res = await apiRequest('/api/v1/admin/settings/on-call-schedule', {
        method: 'PUT',
        body: JSON.stringify({
          entries: entries.map(e => ({
            day_of_week: e.day_of_week,
            start_time: e.start_time,
            end_time: e.end_time,
            timezone: e.timezone,
            is_active: e.is_active !== false,
          })),
        }),
      });
      if (res && res.success && res.data && Array.isArray(res.data.entries)) {
        setEntries(res.data.entries.map(e => ({
          ...e,
          start_time: normalizeTime(e.start_time),
          end_time: normalizeTime(e.end_time),
        })));
        setSaveSuccess(true);
      } else {
        setSaveError((res && res.message) || 'Failed to save schedule');
      }
    } catch (err) {
      setSaveError(err.message || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  }

  if (!isAdmin) { return null; }

  // Group entries by day for display (the ordering mirrors the API's ORDER BY).
  const entriesByDay = Array.from({ length: 7 }, () => []);
  entries.forEach((entry, index) => {
    if (entry.day_of_week >= 0 && entry.day_of_week <= 6) {
      entriesByDay[entry.day_of_week].push({ ...entry, _index: index });
    }
  });

  const noSchedule = entries.length === 0 && !loading;

  return (
    <section
      data-testid="on-call-schedule-card"
      style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        padding: '24px',
        marginBottom: '24px',
      }}
    >
      <h2 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600 }}>
        On-Call Schedule
      </h2>
      <p style={{ margin: '0 0 20px', color: '#4b5563', fontSize: '14px', lineHeight: '1.5' }}>
        Set your on-call hours. You&apos;ll receive operational alerts during these
        windows. If no schedule is set, you receive all alerts.
      </p>

      {loading && <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading…</p>}

      {loadError && (
        <p data-testid="load-error" style={{ color: '#b91c1c', fontSize: '14px' }} role="alert">
          {loadError}
        </p>
      )}

      {!loading && !loadError && noSchedule && (
        <p
          data-testid="no-schedule-message"
          style={{
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            color: '#92400e',
            padding: '10px 12px',
            borderRadius: '8px',
            fontSize: '13px',
            margin: '0 0 16px',
          }}
        >
          No schedule configured — you will receive all ops alerts at all times.
        </p>
      )}

      {!loading && !loadError && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {DAY_LABELS.map((label, dayIndex) => (
            <div
              key={label}
              data-testid={`day-${dayIndex}`}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <strong style={{ fontSize: '14px' }}>{label}</strong>
                <button
                  data-testid={`add-window-${dayIndex}`}
                  type="button"
                  onClick={() => addEntry(dayIndex)}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: '#f9fafb',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  + Add window
                </button>
              </div>

              {entriesByDay[dayIndex].length === 0 ? (
                <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>
                  Off-call
                </p>
              ) : (
                entriesByDay[dayIndex].map(entry => (
                  <div
                    key={`${entry._index}`}
                    data-testid={`entry-${entry._index}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 0',
                      opacity: entry.is_active === false ? 0.55 : 1,
                    }}
                  >
                    <input
                      data-testid={`start-${entry._index}`}
                      type="time"
                      value={entry.start_time}
                      onChange={(e) => updateEntry(entry._index, { start_time: e.target.value })}
                      style={{ padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
                    />
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>to</span>
                    <input
                      data-testid={`end-${entry._index}`}
                      type="time"
                      value={entry.end_time}
                      onChange={(e) => updateEntry(entry._index, { end_time: e.target.value })}
                      style={{ padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
                    />
                    <select
                      data-testid={`tz-${entry._index}`}
                      value={entry.timezone || DEFAULT_TIMEZONE}
                      onChange={(e) => updateEntry(entry._index, { timezone: e.target.value })}
                      style={{ padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
                    >
                      {TIMEZONE_OPTIONS.map(tz => (
                        <option key={tz} value={tz}>{tz}</option>
                      ))}
                      {/* Allow round-tripping a value the user previously saved that isn't in the short list. */}
                      {entry.timezone && !TIMEZONE_OPTIONS.includes(entry.timezone) && (
                        <option value={entry.timezone}>{entry.timezone}</option>
                      )}
                    </select>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
                      <input
                        data-testid={`active-${entry._index}`}
                        type="checkbox"
                        checked={entry.is_active !== false}
                        onChange={(e) => updateEntry(entry._index, { is_active: e.target.checked })}
                      />
                      Active
                    </label>
                    <button
                      data-testid={`remove-${entry._index}`}
                      type="button"
                      onClick={() => removeEntry(entry._index)}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #fecaca',
                        backgroundColor: '#fef2f2',
                        color: '#b91c1c',
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))
              )}
            </div>
          ))}

          {saveError && (
            <p data-testid="save-error" style={{ color: '#b91c1c', fontSize: '14px', margin: 0 }} role="alert">
              {saveError}
            </p>
          )}
          {saveSuccess && (
            <p data-testid="save-success" style={{ color: '#047857', fontSize: '14px', margin: 0 }} role="status">
              Schedule saved.
            </p>
          )}

          <div>
            <button
              data-testid="save-on-call-schedule"
              onClick={handleSave}
              disabled={saving || !entriesValid()}
              type="button"
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: saving || !entriesValid() ? '#9ca3af' : '#111827',
                color: '#fff',
                cursor: saving || !entriesValid() ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {saving ? 'Saving…' : 'Save schedule'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
